/** Keyra hosted OAuth SDK — the same integration the other Ciright apps use. */
import { safeReturnTo } from './auth';

const SDK_SRC = 'https://auth.keyra.ie/sdk/keyra-oauth.js';
const NEXT_KEY = 'universe_login_next';

export interface KeyraResult {
  access_token?: string;
  verification_token?: string;
  userinfo?: unknown;
}

interface KeyraOptions {
  clientId: string;
  authOrigin: string;
  redirectUri: string;
  scope: 'verify';
  onSuccess: (result: KeyraResult) => void;
  onError: (err: unknown) => void;
}

export interface KeyraOAuthApi {
  renderButton(
    target: string | HTMLElement,
    options: KeyraOptions & { mode: 'redirect' | 'popup'; appearance?: { theme?: string; text?: string } }
  ): { detach?: () => void };
  completeRedirectCallback(options: KeyraOptions): void;
}

declare global {
  interface Window {
    KeyraOAuth?: KeyraOAuthApi;
  }
}

export function keyraRedirectUri(configured: string | null): string {
  return configured || `${window.location.origin}/callback`;
}

/** Where to land after Keyra sends the visitor back; same-origin paths only. */
export function storeLoginNext(path: string | null) {
  try {
    const safe = safeReturnTo(path);
    if (safe && !safe.startsWith('/callback')) sessionStorage.setItem(NEXT_KEY, safe);
    else sessionStorage.removeItem(NEXT_KEY);
  } catch {
    /* ignore */
  }
}

export function consumeLoginNext(): string {
  try {
    const value = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return safeReturnTo(value) ?? '/';
  } catch {
    return '/';
  }
}

let sdk: Promise<KeyraOAuthApi> | null = null;

export function loadKeyraSdk(): Promise<KeyraOAuthApi> {
  if (window.KeyraOAuth) return Promise.resolve(window.KeyraOAuth);
  sdk ??= new Promise<KeyraOAuthApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.dataset.keyraOauth = '1';
    script.onload = () => (window.KeyraOAuth ? resolve(window.KeyraOAuth) : reject(new Error('Keyra SDK failed to initialize')));
    script.onerror = () => reject(new Error('Failed to load Keyra SDK'));
    document.body.appendChild(script);
  }).catch((err) => {
    sdk = null;
    throw err;
  });
  return sdk;
}
